import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { colors } from '../../theme/colors';
import { Divider as DSDivider } from '../divider/Divider';

// Types
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
export type HeadingWeight = 'normal' | 'medium' | 'semibold' | 'bold';
export type HeadingColor = 'default' | 'primary' | 'secondary' | 'muted' | 'error' | 'success' | 'warning' | 'info' | 'inherit';
export type HeadingAlign = 'left' | 'center' | 'right';

export interface HeadingLinkProps {
    text: string;
    href?: string;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    external?: boolean;
}

export interface SubheadingProps {
    text: React.ReactNode;
    link?: HeadingLinkProps;
    color?: HeadingColor | string;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

export interface PageHeadingProps {
    /** Main heading text */
    title: React.ReactNode;
    /** Heading level (h1-h6) */
    level?: HeadingLevel;
    /** Custom size override */
    size?: HeadingSize;
    /** Font weight */
    weight?: HeadingWeight;
    /** Text color */
    color?: HeadingColor | string;
    /** Text alignment */
    align?: HeadingAlign;
    /** Subheading/description */
    subheading?: React.ReactNode | SubheadingProps;
    /** Additional action elements (buttons, links) */
    actions?: React.ReactNode;
    /** Whether to show divider below */
    divider?: boolean;
    /** Remove bottom margin */
    noMargin?: boolean;
    /** Custom className */
    className?: string;
    /** ID for anchor linking */
    id?: string;
}

export interface SectionHeadingProps extends Omit<PageHeadingProps, 'level'> {
    /** Heading level (defaults to 2 for sections) */
    level?: HeadingLevel;
}

export interface HeadingProps {
    /** Heading content */
    children: React.ReactNode;
    /** Heading level (h1-h6) */
    level?: HeadingLevel;
    /** Render as different element */
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
    /** Custom size override */
    size?: HeadingSize;
    /** Font weight */
    weight?: HeadingWeight;
    /** Text color */
    color?: HeadingColor | string;
    /** Text alignment */
    align?: HeadingAlign;
    /** Remove bottom margin */
    noMargin?: boolean;
    /** Custom className */
    className?: string;
    /** ID for anchor linking */
    id?: string;
}

// Size configurations
const sizeClasses: Record<HeadingSize, string> = {
    'xs': 'text-xs leading-tight',
    'sm': 'text-sm leading-tight',
    'md': 'text-base leading-snug',
    'lg': 'text-lg leading-snug',
    'xl': 'text-xl leading-snug',
    '2xl': 'text-2xl leading-tight',
    '3xl': 'text-3xl leading-tight',
    '4xl': 'text-4xl leading-tight',
};

// Level to size mapping
const levelSizeMap: Record<HeadingLevel, HeadingSize> = {
    1: '3xl',
    2: '2xl',
    3: 'xl',
    4: 'lg',
    5: 'md',
    6: 'sm',
};

// Weight classes
const weightClasses: Record<HeadingWeight, string> = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
};

// Alignment classes
const alignClasses: Record<HeadingAlign, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

// Color mapping
const colorMap: Record<HeadingColor, string> = {
    default: colors.textDefault,
    primary: colors.primary,
    secondary: colors.textMuted,
    muted: colors.textWeak,
    error: colors.error,
    success: colors.success,
    warning: colors.warning,
    info: colors.info,
    inherit: 'inherit',
};

// Resolve color
const resolveColor = (color: HeadingColor | string): string => {
    if (color in colorMap) {
        return colorMap[color as HeadingColor];
    }
    return color;
};

// Subheading size classes
const subheadingSizeClasses: Record<'sm' | 'md' | 'lg', string> = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
};

/**
 * HeadingLink - Link component for use within headings
 */
export const HeadingLink: React.FC<HeadingLinkProps & { className?: string }> = ({
    text,
    href,
    onClick,
    external = false,
    className = '',
}) => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
        <a
            href={href}
            onClick={onClick}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className={cn(
                'font-medium transition-colors duration-150 cursor-pointer',
                isHovered ? 'underline' : 'no-underline',
                className
            )}
            style={{
                color: isHovered ? colors.primaryHover : colors.primary,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {text}
        </a>
    );
};

/**
 * Subheading - Description text below heading with optional link
 */
export const Subheading: React.FC<SubheadingProps> = ({
    text,
    link,
    color = 'muted',
    size = 'md',
    className = '',
}) => {
    return (
        <p
            className={cn(
                'mt-1',
                subheadingSizeClasses[size],
                className
            )}
            style={{ color: resolveColor(color) }}
        >
            {text}
            {link && (
                <>
                    {' '}
                    <HeadingLink
                        text={link.text}
                        href={link.href}
                        onClick={link.onClick}
                        external={link.external}
                        className={subheadingSizeClasses[size]}
                    />
                </>
            )}
        </p>
    );
};

/**
 * Heading - Base heading component
 */
export const Heading: React.FC<HeadingProps> = ({
    children,
    level = 1,
    as,
    size,
    weight = 'semibold',
    color = 'default',
    align = 'left',
    noMargin = false,
    className = '',
    id,
}) => {
    const Component = (as || `h${level}`) as React.ElementType;
    const resolvedSize = size || levelSizeMap[level];

    return React.createElement(
        Component,
        {
            id,
            className: cn(
                sizeClasses[resolvedSize],
                weightClasses[weight],
                alignClasses[align],
                !noMargin && 'mb-2',
                className
            ),
            style: { color: resolveColor(color) },
        },
        children
    );
};

/**
 * PageHeading - Full page title with optional subheading and actions
 */
export const PageHeading: React.FC<PageHeadingProps> = ({
    title,
    level = 1,
    size,
    weight = 'bold',
    color = 'default',
    align = 'left',
    subheading,
    actions,
    divider = true,
    noMargin = false,
    className = '',
    id,
}) => {
    const renderSubheading = () => {
        if (!subheading) return null;

        if (typeof subheading === 'string' || React.isValidElement(subheading)) {
            return (
                <p
                    className="mt-1 text-sm"
                    style={{ color: colors.textMuted }}
                >
                    {subheading}
                </p>
            );
        }

        const subheadingProps = subheading as SubheadingProps;
        return <Subheading {...subheadingProps} className="mt-1" />;
    };

    return (
        <div className={cn('w-full', !noMargin, className)}>
            <div className={cn(
                'flex items-start gap-4',
                align === 'center' && 'justify-center',
                align === 'right' && 'justify-end',
                actions && 'justify-between'
            )}>
                <div className={cn(alignClasses[align], !actions && 'flex-1')}>
                    <Heading
                        level={level}
                        size={size || '2xl'}
                        weight={weight}
                        color={color}
                        align={align}
                        noMargin
                        id={id}
                    >
                        {title}
                    </Heading>
                    {renderSubheading()}
                </div>
                {actions && (
                    <div className="flex items-center gap-2 shrink-0">
                        {actions}
                    </div>
                )}
            </div>
            {divider && <DSDivider marginTop={12} />}
        </div>
    );
};

/**
 * SectionHeading - Section title with optional subheading
 */
export const SectionHeading: React.FC<SectionHeadingProps> = ({
    title,
    level = 2,
    size,
    weight = 'semibold',
    color = 'default',
    align = 'left',
    subheading,
    actions,
    divider = false,
    noMargin = false,
    className = '',
    id,
}) => {
    const renderSubheading = () => {
        if (!subheading) return null;

        if (typeof subheading === 'string' || React.isValidElement(subheading)) {
            return (
                <p
                    className="mt-1 text-sm"
                    style={{ color: colors.textMuted }}
                >
                    {subheading}
                </p>
            );
        }

        const subheadingProps = subheading as SubheadingProps;
        return <Subheading {...subheadingProps} className="mt-1" />;
    };

    return (
        <div
            className={cn(
                'w-full',
                !noMargin && 'mb-4',
                divider && 'pb-3 border-b',
                className
            )}
            style={divider ? { borderColor: colors.surfaceSubtle } : undefined}
        >
            <div className={cn(
                'flex items-start gap-3',
                align === 'center' && 'justify-center',
                align === 'right' && 'justify-end',
                actions && 'justify-between'
            )}>
                <div className={cn(alignClasses[align], !actions && 'flex-1')}>
                    <Heading
                        level={level}
                        size={size || 'xl'}
                        weight={weight}
                        color={color}
                        align={align}
                        noMargin
                        id={id}
                    >
                        {title}
                    </Heading>
                    {renderSubheading()}
                </div>
                {actions && (
                    <div className="flex items-center gap-2 shrink-0">
                        {actions}
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * CardHeading - Compact heading for cards
 */
export const CardHeading: React.FC<Omit<SectionHeadingProps, 'divider'>> = ({
    level = 3,
    size,
    weight = 'semibold',
    noMargin = false,
    ...props
}) => (
    <SectionHeading
        level={level}
        size={size || 'lg'}
        weight={weight}
        noMargin={noMargin}
        {...props}
    />
);

/**
 * FormHeading - Heading for form sections
 */
export const FormHeading: React.FC<{
    title: React.ReactNode;
    description?: React.ReactNode;
    required?: boolean;
    className?: string;
}> = ({
    title,
    description,
    required = false,
    className = '',
}) => (
        <div className={cn('mb-4', className)}>
            <h4
                className="text-base font-semibold flex items-center gap-1"
                style={{ color: colors.textDefault }}
            >
                {title}
                {required && (
                    <span style={{ color: colors.error }}>*</span>
                )}
            </h4>
            {description && (
                <p
                    className="mt-1 text-sm"
                    style={{ color: colors.textMuted }}
                >
                    {description}
                </p>
            )}
        </div>
    );

export interface ModalHeadingProps {
    /** Main heading text */
    title: React.ReactNode;
    /** Optional description below title */
    description?: React.ReactNode;
    /** Close button handler - if provided, shows close icon */
    onClose?: () => void;
    /** Close icon size (default: 24) */
    closeIconSize?: number;
    /** Text alignment */
    align?: 'left' | 'center' | 'right';
    /** Remove bottom margin */
    noMargin?: boolean;
    /** Custom className */
    className?: string;
}

/**
 * ModalHeading - Heading for modals/dialogs
 */
export const ModalHeading: React.FC<ModalHeadingProps> = ({
    title,
    description,
    onClose,
    closeIconSize = 24,
    align = 'center',
    noMargin = false,
    className = '',
}) => {
    const alignClasses = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
    };

    return (
        <div className={cn('relative', !noMargin && 'mb-4', className)}>
            {onClose && (
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors hover:bg-gray-100"
                    style={{ color: colors.textMuted }}
                    aria-label="Close"
                >
                    <X size={closeIconSize} />
                </button>
            )}
            <div className={alignClasses[align]}>
                <h3
                    className="text-lg font-semibold"
                    style={{ color: colors.textDefault }}
                >
                    {title}
                </h3>
                {description && (
                    <p
                        className="mt-1 text-sm"
                        style={{ color: colors.textMuted }}
                    >
                        {description}
                    </p>
                )}
            </div>
        </div>
    );
};

/**
 * StepHeading - Heading with step number
 */
export const StepHeading: React.FC<{
    step: number;
    title: React.ReactNode;
    description?: React.ReactNode;
    totalSteps?: number;
    className?: string;
}> = ({
    step,
    title,
    description,
    totalSteps,
    className = '',
}) => (
        <div className={cn('flex items-start gap-3 mb-4', className)}>
            <div
                className="flex items-center justify-center w-8 h-8 rounded-full shrink-0 text-sm font-semibold"
                style={{
                    backgroundColor: colors.primaryExtraLight,
                    color: colors.primary,
                }}
            >
                {step}
            </div>
            <div className="flex-1 pt-0.5">
                <h4
                    className="text-base font-semibold flex items-center gap-2"
                    style={{ color: colors.textDefault }}
                >
                    {title}
                    {totalSteps && (
                        <span
                            className="text-xs font-normal"
                            style={{ color: colors.textMuted }}
                        >
                            (Step {step} of {totalSteps})
                        </span>
                    )}
                </h4>
                {description && (
                    <p
                        className="mt-1 text-sm"
                        style={{ color: colors.textMuted }}
                    >
                        {description}
                    </p>
                )}
            </div>
        </div>
    );

/**
 * EmptyStateHeading - Heading for empty states
 */
export const EmptyStateHeading: React.FC<{
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: React.ReactNode;
    actions?: React.ReactNode;
    className?: string;
}> = ({
    title,
    description,
    icon,
    actions,
    className = '',
}) => (
        <div className={cn('text-center py-8', className)}>
            {icon && (
                <div
                    className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full"
                    style={{ backgroundColor: colors.surfaceLighter }}
                >
                    {icon}
                </div>
            )}
            <h3
                className="text-lg font-semibold"
                style={{ color: colors.textDefault }}
            >
                {title}
            </h3>
            {description && (
                <p
                    className="mt-2 text-sm max-w-md mx-auto"
                    style={{ color: colors.textMuted }}
                >
                    {description}
                </p>
            )}
            {actions && (
                <div className="mt-4 flex items-center justify-center gap-2">
                    {actions}
                </div>
            )}
        </div>
    );

export default Heading;
